/**
 * Analyzes a receipt image/PDF using Claude Vision via Supabase Edge Function.
 * Returns structured line items ready for encomienda_items / shipment_items.
 *
 * @param {File} file - JPEG, PNG, or PDF receipt (max 5MB)
 * @returns {Promise<ReceiptAnalysisResult>}
 *
 * Routes through: supabase/functions/v1/analyze-receipt
 * The Anthropic API key lives only in Edge Function secrets — never in the browser.
 *
 * Required Vercel env vars:
 *   REACT_APP_SUPABASE_URL      — your Supabase project URL
 *   REACT_APP_SUPABASE_ANON_KEY — your anon key
 *
 * DO NOT add REACT_APP_ANTHROPIC_API_KEY to Vercel.
 * The API key lives in Supabase Edge Function secrets only.
 *
 * In Supabase Edge Function secrets:
 *   ANTHROPIC_API_KEY — your Anthropic API key
 */
export async function analyzeReceipt(file) {
  const base64 = await fileToBase64(file);
  const mediaType = file.type; // image/jpeg | image/png | application/pdf

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Supabase config missing — check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env'
    );
  }

  console.log('[analyzeReceipt] routing to Edge Function:', `${supabaseUrl}/functions/v1/analyze-receipt`);
  console.log('[analyzeReceipt] anonKey present:', !!supabaseAnonKey);

  const response = await fetch(
    `${supabaseUrl}/functions/v1/swift-endpoint`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ base64, mediaType })
    }
  );

  console.log('[analyzeReceipt] response status:', response.status);

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      'Analysis service error: ' + (err.error || response.status)
    );
  }

  const data = await response.json();

  if (!data.content || !Array.isArray(data.content)) {
    throw new Error('Unexpected response format from analysis service');
  }

  const text = data.content
    .filter(b => b.type === 'text')
    .map(b => b.text)
    .join('');

  // Strip any accidental markdown fences
  const clean = text.replace(/```json/g, '').replace(/```/g, '').trim();

  try {
    const result = JSON.parse(clean);
    if (result.error) {
      return { success: false, error: result.error, items: [] };
    }
    return {
      success: true,
      ...result,
      items: (result.items || []).map((item, i) => ({
        ...item,
        id: 'ai-' + Date.now() + '-' + i,
        description: item.description ?? item.descripcion ?? item.name ?? item.product_name ?? '',
        quantity: item.quantity ?? item.cantidad ?? item.qty ?? 1,
        unit_price: item.unit_price ?? item.valor_unitario ?? item.unit_value ?? item.price ?? 0,
        condition: 'NEW',
        confirmed: false, // staff must confirm each item
      })),
    };
  } catch (e) {
    console.error('[analyzeReceipt] Parse error:', e, 'Raw:', clean);
    return { success: false, error: 'Could not parse AI response', items: [] };
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove "data:image/jpeg;base64," prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
